import { useEffect } from "react";

/**
 * KeepaliveMonitor — fires a cheap `GET /api/health` ping every 3 minutes
 * to keep the backend pod warm and the ingress proxy's idle-eviction
 * timer pushed out. Mounted once at the App root next to <Toaster /> +
 * <AuthHealthMonitor />.
 *
 * Why this exists (Feb 22 2026 · perf audit P3): Emergent's preview
 * proxy and (likely) prod-side idle policies will spin pods down or
 * route through a cold path after some minutes of no traffic. The first
 * real user request after that pays a 3–8s cold-start tax. A
 * background ping every 3 minutes is cheap insurance — `/api/health`
 * does NOT touch the database (verified at audit time) and returns a
 * single tiny JSON object, so the cost is a sub-1KB HTTP round-trip
 * per tab per 3 minutes.
 *
 * Guard-rails baked in:
 *  - Skips while document.visibilityState !== "visible" so a backgrounded
 *    tab doesn't keep the pod warm for free (and doesn't waste the
 *    user's battery / data).
 *  - Fails silently on network errors — this is fire-and-forget; any
 *    failure is the same outcome as not pinging at all (next 3-min
 *    cycle will try again).
 *  - Uses the same REACT_APP_BACKEND_URL the rest of the app uses, so
 *    behaviour mirrors production routing (CDN, ingress, etc.) and
 *    actually warms the path users traverse.
 *  - Renders null — no DOM impact.
 */
const PING_MS = 3 * 60 * 1000;

const KeepaliveMonitor = () => {
  useEffect(() => {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    if (!base) return;
    const url = `${base}/api/health`;

    const ping = () => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      // Fetch with explicit no-store so the ping always hits the network,
      // never a service worker / browser cache layer.
      fetch(url, { method: "GET", cache: "no-store", credentials: "omit" })
        .catch(() => { /* fire-and-forget */ });
    };

    // Don't ping on mount — the app's own initial requests already
    // warm the pod. First keepalive fires 3 minutes in.
    const t = setInterval(ping, PING_MS);
    // Also ping when the tab is refocused after being hidden — the pod
    // may have gone cold while the user was away.
    const onVisible = () => { if (document.visibilityState === "visible") ping(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  return null;
};

export default KeepaliveMonitor;
